// ─────────────────────────────────────────────────────────────────────────────
// Accounting — shared logic & display data (DESIGN-CONCEPT-MOBILE v2 §1.1).
//
// The desktop table (`Accounting.web.jsx`) and the mobile report
// (`Accounting.mobile.jsx`) are two independent render trees, but they must
// never *disagree* on what a line means, how a date reads, what a CSV row
// contains, or which filter is the default. All of that non-visual truth lives
// here — imported by both surfaces so a change is made once.
//
// Markup (badges, rows, cards) is NOT here: a component belongs to exactly one
// surface. This module only holds data maps and pure functions.
// ─────────────────────────────────────────────────────────────────────────────

import { Stethoscope, FlaskConical, Package, CircleDashed } from 'lucide-react';
import { C, soft } from '@parsina/framework/clinical/src/constants/theme';
import { rialToToman } from '../../lib/money';
import { toDisplay } from '@parsina/framework/ui/lib/currency';
import { currencyOf, isIrr } from '../../lib/clinicMoney';
import { STATUS } from '@parsina/framework/clinical/src/constants/statuses';
import { accountingLineLabel } from '../../lib/ledgerVocabulary';

// Soft tints are DERIVED from the brand tokens (never a raw hex literal): the token
// colour dropped to low alpha. Same token-tint pattern the KpiCard `bg` prop uses.
export { soft };

// Item type → label, lucide icon, brand-token colour. No emoji-as-icon.
export const ITEM_TYPE_META = (t) => ({
  VISIT:        { label: t('patient.visit'),      Icon: Stethoscope,  color: C.navy },
  MEDICAL_SERVICE: { label: t('accounting.paraclinical'), Icon: FlaskConical, color: C.teal },
  PRODUCT:      { label: t('accounting.goods'),  Icon: Package,      color: C.amber },
  // A line the accounting service could not type (unset/unknown). Rendered with a
  // neutral label so an untyped line reads honestly rather than as raw "OTHER".
  OTHER:        { label: t('common.state.other'),       Icon: CircleDashed, color: C.textSecondary },
});

// Settlement line-status label from the one status source.
export const settlementLabel = (s) => STATUS[s]?.label || s;

// The settlement tabs, mirroring the clinic cashier's status tabs.
export const TABS = (t) => ([
  { id: 'ALL',     label: t('common.state.all') },
  { id: 'PENDING', label: t('accounting.awaitingSettlement') },
  { id: 'SETTLED', label: t('common.settlement.SETTLED') },
]);

export const TYPE_OPTIONS = (t) => ([
  { value: 'VISIT',        label: t('patient.visit') },
  { value: 'MEDICAL_SERVICE', label: t('accounting.paraclinical') },
  { value: 'PRODUCT',      label: t('accounting.goods') },
]);

// Filter defaults — a param at its default is dropped from the URL on desktop and
// reads as "no active filter" on both surfaces.
export const DEFAULTS = { settle: 'ALL', q: '', clinic: '', type: '', preset: 'month', from: '', to: '' };

// A plain module cannot call a hook, so the locale's date formatter is threaded
// in by the calling component (the `faNum(n, formatDigits)` convention). Rendered
// as the named `long` shape — the house standard — rather than a hand-built
// Persian string, so an English or Arabic UI reads its own digits and months.
export const fmtDate = (ms, formatDate) => (formatDate ? formatDate(Number(ms), 'long') : '—');

// ── Money text that is not a bare figure ─────────────────────────────────────
// Two catalogue sentences carry the toman word INSIDE the string — «سهم شما:
// {{amount}} ت», «{{amount}} ت» — so they can only ever describe IRR. IRR keeps
// them exactly; another currency is assembled from unit-neutral pieces and the
// currency's own short unit («AED»), never a toman word. `formatMoney` and
// `moneyUnit` are threaded in from the calling component's `useTranslation()`.
export function amountText(amount, currency, t, formatMoney, moneyUnit) {
  if (isIrr(currency)) return t('accounting.tomanAmount', { amount: formatMoney(amount) });
  return `${formatMoney(amount, { currency })} ${moneyUnit(currency, 'short')}`;
}

export function yourShareText(amount, currency, t, formatMoney, moneyUnit) {
  if (isIrr(currency)) return t('accounting.yourShareAmount', { amount: formatMoney(amount) });
  return `${t('accounting.yourShareLabel')} ${amountText(amount, currency, t, formatMoney, moneyUnit)}`;
}

// ── CSV export ────────────────────────────────────────────────────────────────
// Persian-headed CSV with a UTF-8 BOM so Excel opens it with the right encoding.
// Exports whatever line set it is handed — i.e. the currently filtered view.
//
// Two shapes, decided by the rows:
//   • every row IRR (every doctor today) → the file is byte-for-byte what it has
//     always been: toman figures under «(تومان)» headers.
//   • any row in another currency → a currency column is added after the count,
//     the amount headers lose their toman word (they are the screen's own column
//     titles), and each row's figures are in ITS OWN currency's display unit. No
//     column is ever summable across rows of two currencies without that column,
//     which is the point of having it.
//
// The currency column's header is `accounting.currency` («ارز» / Currency /
// العملة), and the share column is `accounting.doctorShare` — the toman-free
// twin of `doctorShareToman`, so a mixed export names the same column as an
// IRR-only one, minus the unit.
export function exportLinesCsv(lines, rangeLabel, t, formatDate) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const allIrr = lines.every((l) => isIrr(l.currency));
  // Toman, like the screen. Plain digits, not fa-IR groups: Excel reads «۵۰۰٬۰۰۰» as
  // text, not as a number an accountant can sum.
  const headers = allIrr
    ? [t('common.labels.date'), t('common.labels.clinic'), t('common.labels.patient'), t('accounting.serviceType'), t('accounting.description'), t('common.labels.count'),
       t('accounting.totalAmountToman'), t('accounting.doctorShareToman'), t('accounting.settlementStatus'), t('accounting.performedBy')]
    : [t('common.labels.date'), t('common.labels.clinic'), t('common.labels.patient'), t('accounting.serviceType'), t('accounting.description'), t('common.labels.count'),
       t('accounting.currency'), t('accounting.totalAmount'), t('accounting.doctorShare'), t('accounting.settlementStatus'), t('accounting.performedBy')];
  const rows = lines.map((l) => {
    const currency = currencyOf(l);
    const money = allIrr
      ? [rialToToman(l.grossAmount) ?? '', l.doctorShare == null ? '' : rialToToman(l.doctorShare)]
      : [currency, toDisplay(l.grossAmount, currency) ?? '', l.doctorShare == null ? '' : toDisplay(l.doctorShare, currency)];
    return [
      fmtDate(l.createdAt, formatDate),
      l.clinicName || '—',
      l.patientName || '',
      (ITEM_TYPE_META(t)[l.itemType] || {}).label || l.itemType,
      accountingLineLabel(l.description, t),
      l.quantity,
      ...money,
      settlementLabel(l.settlementStatus),
      l.performedByDoctor ? t('common.labels.doctor') : t('accounting.clinicStaff'),
    ].map(esc).join(',');
  });

  const csv = '﻿' + [headers.join(','), ...rows].join('\r\n');
  const filename = `${t('patient.files.accountingExport', { range: (rangeLabel || t('accounting.report')).replace(/\s+/g, '_') })}.csv`;
  return deliverCsv(csv, filename);
}

// ─────────────────────────────────────────────────────────────────────────────
// Getting the file to the doctor — spec finding 19.
//
// An `<a download>` is the right mechanism in a browser and a DEAD ONE inside
// an installed iOS PWA: standalone mode has no download UI at all, so the click
// does nothing, silently, and the doctor is left believing the export failed
// halfway. Same story for a blob URL opened in a new tab.
//
// The Web Share API is the platform's own answer — it hands the file to the
// share sheet, where «ذخیره در Files», Mail and everything else already live.
// `canShare({ files })` is the only honest test: Safari exposes `share` far more
// widely than it accepts files.
//
// Three outcomes, and the caller needs to tell them apart:
//   'shared'    the share sheet took it
//   'downloaded' the anchor path ran (a real browser)
//   'unsupported' neither is available — the screen says «در نسخهٔ دسکتاپ»
//                 rather than pretending something happened
// ─────────────────────────────────────────────────────────────────────────────
export async function deliverCsv(csv, filename) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

  try {
    const file = new File([blob], filename, { type: 'text/csv' });
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: filename });
      return 'shared';
    }
  } catch (err) {
    // `AbortError` is the doctor dismissing the sheet — a decision, not a
    // failure, and falling through to a download would override it.
    if (err?.name === 'AbortError') return 'shared';
    // Anything else: fall through and try the anchor.
  }

  // A standalone PWA has no download UI, so an anchor there is a no-op that
  // reports success. Say so instead.
  const standalone = typeof window !== 'undefined'
    && (window.matchMedia?.('(display-mode: standalone)')?.matches
      || window.navigator?.standalone === true);
  if (standalone) return 'unsupported';

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return 'downloaded';
}
